// ***************************************
// tgfGraphics
//
// This object contains properties and methods related to the
// graphics, like dealing with canvasses and some useful generic
// drawing methods
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 26-6-2015
// ***************************************
import { tbGame } from "../engine/tbGame";
import { tbGraphics } from "../engine/tbGraphics";
import { PIXI } from "../engine/tbPIXI";
import { tgfSettings } from "./tgfSettings";
import { tgfFunctions } from "./tgfFunctions";
import { tbBrowser } from "../engine/tbBrowser";
import { Event } from "@cg-package/coolkit-utilities";
import { mainContainer } from "../engine/tbMainContainer";
tgfSettings.graphicDefaults = {
    mainCanvasId: "canvas",
    containerId: "game_container",
    areaWidth: 1152,
    areaHeight: 640,
    createBackgroundCanvas: true,
    safeZoneWidth: 850,
    safeZoneHeight: 640,
    safeZoneTop: 0,
    safeZoneLeft: 151,
    safeZoneHorizontalAnchor: "center",
    safeZoneVerticalAnchor: "middle",
    allowScalingUp: true,
    allowScalingDown: true,
    backgroundImage: undefined,
    // backgroundColor: "white",				// background color
    autoUpdateInterval: 1,
    autoUpdateForce: false,
    view: null,
    transparent: true,
    autoResize: false,
    antialias: false,
    forceFXAA: false,
    resolution: 1,
    backgroundColor: 0x000000,
    clearBeforeRender: true,
    preserveDrawingBuffer: false,
    roundPixels: false
};
var TGFGraphics = /** @class */ (function () {
    function TGFGraphics() {
        this._initialized = false;
        this.onCanvasChange = new Event();
        /** Array of all canvasses in order of depth (lowest one first) */
        this.canvasses = [];
        this.renderers = [];
        this.pixiContainer = mainContainer;
        this.autoUpdateInterval = null;
    }
    // ***************************************
    // Initialization
    // ***************************************
    // Initialize the graphics, creating the default canvasses
    TGFGraphics.prototype.initialize = function (settings) {
        var _this = this;
        // Set the properties from the settings
        this._initialized = true;
        this.canvasses = [];
        this._setProperties(tgfSettings.graphicDefaults);
        this._setProperties(settings);
        // get or create DIV element to contain canvasses
        this.container = document.getElementById(this.containerId);
        if (!this.container) {
            this.container = document.createElement("DIV");
            this.container.id = this.containerId;
            document.body.appendChild(this.container);
        }
        var fontCanvas = this.addCanvas();
        tbGraphics.fontCanvas = fontCanvas;
        var options = {
            view: this.view,
            transparent: this.transparent,
            autoResize: this.autoResize,
            antialias: this.antialias,
            forceFXAA: this.forceFXAA,
            resolution: this.resolution,
            backgroundColor: this.backgroundColor,
            clearBeforeRender: this.clearBeforeRender,
            preserveDrawingBuffer: this.preserveDrawingBuffer,
            roundPixels: this.roundPixels
        };
        // PIXI.glCore.VertexArrayObject.FORCE_NATIVE = true;
        // PIXI.settings.SPRITE_MAX_TEXTURES = 1;
        // get or create main canvas and set the properties
        this.renderer = new PIXI["autoDetectRenderer"](tgfGraphics.areaWidth, tgfGraphics.areaHeight, options);
        tbGame.renderCallback = function () { return _this.render(); };
        // disable the pixi ticker to avoid an extra requestAnimationFrame
        var ticker = PIXI["ticker"]["shared"];
        ticker["autoStart"] = false;
        ticker["stop"]();
        this.mainCanvas = this.renderer.view;
        if (!this.mainCanvas) {
            this.mainCanvas = document.createElement("canvas");
            this.container.appendChild(this.mainCanvas);
        }
        else {
            this.addCustomCanvas(this.mainCanvas);
        }
        this._initializeCanvas(this.mainCanvas, { canvasTop: 0, canvasLeft: 0 });
        this.canvasses.push(this.mainCanvas);
        // update the viewport and register the resize event handler to do the same
        window.addEventListener("resize", function () { _this._updateViewport.call(_this, true); }, true);
        if (this.autoUpdateInterval > 0) {
            window.setInterval(function () { _this._updateViewport.call(_this, _this.autoUpdateForce); }, 1000 * this.autoUpdateInterval);
        }
        this._updateViewport(true);
        // set the background color and image
        if (this.backgroundColor !== undefined) {
            this.setBackgroundColor(this.backgroundColor);
        }
        if (this.backgroundImage !== undefined) {
            this.setBackgroundImage(this.backgroundImage);
        }
        // set current canvas properly in the engine
        // tbGraphics.checkWebGL(this.mainCanvas);
        tbGame.setMainCanvas(this.mainCanvas);
        // tbGraphics.checkWebGL(this.mainCanvas);
        // tbGraphics.setCanvas(this.mainCanvas);
        tbGraphics.webGLAvailable = true;
        if (!this.pixiContainer) {
            throw new Error("No renderable pixi container exists");
        }
    };
    // ***************************************
    // Internal methods
    // ***************************************
    // Set a number of properties of the graphics object
    TGFGraphics.prototype._setProperties = function (properties) {
        var property;
        if (typeof properties !== "object") {
            return;
        }
        for (property in properties) {
            if (properties.hasOwnProperty(property)) {
                this[property] = tgfFunctions.copyValue(properties[property]);
            }
        }
    };
    // Determines the size of the container div and updates the scale factor of the canvasses
    TGFGraphics.prototype._updateViewport = function (force) {
        var prefRatio, minRatio, windowRatio, width, height, divWidth, divHeight, widthOutside, widthOutsideFactor, heightOutside, heightOutsideFactor;
        if (!this._initialized) {
            return;
        }
        // Check whether anything must be done
        if (!force && this.windowWidth !== undefined && this.windowWidth === window.innerWidth &&
            this.windowHeight !== undefined && this.windowHeight === window.innerHeight) {
            return;
        }
        // determine the size of the window
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
        // determine the ratios
        var safeZoneWidthDifference = this.safeZoneWidth - this.areaWidth;
        var safeZoneHeightDifference = this.safeZoneHeight - this.areaHeight;
        windowRatio = (this.windowWidth - safeZoneWidthDifference) / (this.windowHeight - safeZoneHeightDifference);
        prefRatio = this.areaWidth / (this.areaHeight);
        minRatio = this.safeZoneWidth / this.safeZoneHeight;
        // Default settings for when the area covers the whole window
        width = this.windowWidth;
        height = this.windowHeight;
        this.scaleFactor = Math.min(this.windowWidth / this.safeZoneWidth, this.windowHeight / this.safeZoneHeight);
        // Screen is too high, take full width, but scale up so complete width is being used by safe zone to achieve maximum height
        if (windowRatio < prefRatio && windowRatio < minRatio) {
            var scaleHeight = this.windowWidth / this.safeZoneWidth;
            if (scaleHeight < this.scaleFactor) {
                height = Math.floor(this.windowWidth / (this.safeZoneWidth / this.areaHeight));
                this.scaleFactor = scaleHeight;
            }
            else {
                height = Math.floor(this.windowWidth / (this.safeZoneWidth / this.areaHeight));
            }
        }
        // Screen is too wide, take full height but scale up so complete height is being used by safe zone to achieve maximum width
        if (windowRatio > prefRatio && windowRatio > minRatio) {
            var scaleWidth = (this.windowHeight - this.safeZoneTop) / (this.safeZoneHeight - this.safeZoneTop);
            if (scaleWidth < this.scaleFactor) {
                width = Math.floor((this.windowHeight - this.safeZoneTop) * (this.areaWidth / (this.safeZoneHeight - this.safeZoneTop)));
                this.scaleFactor = scaleWidth;
            }
            else {
                width = Math.floor((this.windowHeight - this.safeZoneTop) * (this.areaWidth / (this.safeZoneHeight - this.safeZoneTop)));
            }
        }
        // Check if game is allowed to scale
        if (!this.allowScalingUp && this.scaleFactor > 1) {
            width = Math.min(width, this.areaWidth);
            height = Math.min(height, this.areaHeight);
            this.scaleFactor = 1;
        }
        if (!this.allowScalingDown && this.scaleFactor < 1) {
            width = Math.max(width, this.areaWidth);
            height = Math.max(height, this.areaHeight);
            this.scaleFactor = 1;
        }
        // determine total area offsets
        this.cssLeftOffset = 0;
        this.cssTopOffset = 0;
        widthOutside = this.areaWidth - this.windowWidth / this.scaleFactor;
        if (this.areaWidth === this.safeZoneWidth) {
            widthOutsideFactor = 1;
        }
        else {
            widthOutsideFactor = widthOutside / (this.areaWidth - this.safeZoneWidth);
        }
        if (widthOutside >= 1) {
            switch (this.safeZoneHorizontalAnchor) {
                case "right":
                    // move total area as far as possible to the left
                    this.cssLeftOffset = -Math.min(widthOutside, this.safeZoneLeft);
                    break;
                case "left":
                    // only move total area to left if the safe zone on the right is not enough to compensate
                    this.cssLeftOffset = -Math.max(0, widthOutside - this.areaWidth + this.safeZoneWidth + this.safeZoneLeft);
                    break;
                default:
                    // move total area half of the difference between width and window.width to the left
                    this.cssLeftOffset = -widthOutsideFactor * this.safeZoneLeft;
            }
        }
        heightOutside = this.areaHeight - this.windowHeight / this.scaleFactor;
        heightOutsideFactor = heightOutside / (this.areaHeight - this.safeZoneHeight);
        // resize and position the game container
        divHeight = Math.min(this.windowHeight, height);
        divWidth = Math.min(this.windowWidth, width);
        var verticalAlignment = "top:50%; margin-top:" + (-divHeight / 2) + "px";
        switch (this.safeZoneVerticalAnchor) {
            case "bottom":
                // move total area as far as possible to the top
                this.cssTopOffset = -Math.min(heightOutside, this.safeZoneTop);
                break;
            case "top":
                // only move total area to top if the safe zone on the bottom is not enough to compensate
                this.cssTopOffset = -Math.max(0, heightOutside - this.areaHeight + this.safeZoneHeight) + this.safeZoneTop;
                verticalAlignment = "margin-top:" + this.cssTopOffset + "px";
                break;
            default:
                // move total area half of the difference between height and window.height to the top
                this.cssTopOffset = -heightOutsideFactor * this.safeZoneTop;
        }
        this.container.setAttribute("style", "position:fixed; overflow:hidden; z-index: 0; width:" + divWidth + "px; left:50%; margin-left:" + (-divWidth / 2) +
            "px; height: " + divHeight + "px;" + verticalAlignment);
        // set visible area properties
        this.visibleLeft = -this.cssLeftOffset;
        this.visibleTop = -this.cssTopOffset;
        this.visibleWidth = Math.ceil(divWidth / this.scaleFactor);
        this.visibleHeight = Math.ceil(divHeight / this.scaleFactor);
        // resize the canvasses using CSS
        this._updateCanvassesCSS();
        // apply a hack to clear browser back buffer after DOM changes
        this.performBufferClearHack();
        this.onCanvasChange.fire();
    };
    // Pauses the game and then forces a DOM update to clear browser back buffers
    // See Confluence Android Browser Canvas Redraw Bug Fix
    TGFGraphics.prototype.performBufferClearHack = function () {
        var _this = this;
        var useHack, context, canvas, images, image;
        // first check whether hack should be applied (only on Android stock browser version4 and higher)
        useHack = tbBrowser.browser.stockandroid && tbBrowser.browser.versionAsNumber && tbBrowser.browser.versionAsNumber >= 4;
        if (tbGraphics.webGLAvailable) {
            return;
        }
        if (!useHack) {
            return;
        }
        if (this._DOMUpdating) {
            return;
        }
        this._DOMUpdating = true;
        // pause game
        tgfFunctions.pauseAll();
        // copy canvas image data and clear canvasses
        images = [];
        for (var i = 0; i < this.canvasses.length; i += 1) {
            canvas = this.canvasses[i];
            context = canvas.getContext("2d");
            try {
                image = context.getImageData(0, 0, canvas.width, canvas.height);
                images.push(image);
            }
            catch (e) {
                if (!PROD) {
                    console.log("error in getting image data: " + e.name + ", " + e.message);
                }
            }
            context.clearRect(0, 0, canvas.width, canvas.height);
            canvas.style.visibility = "hidden";
        }
        // set almost immediate timeout to force DOM refresh
        setTimeout(function () {
            for (var i = 0; i < _this.canvasses.length; i += 1) {
                _this.canvasses[i].style.visibility = "visible";
            }
        }, 1);
        // set slightly later timeout to redraw image data
        setTimeout(function () {
            // redraw image data per canvas
            for (var i = 0; i < _this.canvasses.length; i += 1) {
                context = _this.canvasses[i].getContext("2d");
                try {
                    context.putImageData(images[i], 0, 0);
                }
                catch (e) {
                    if (!PROD) {
                        console.log("error in putting image data: " + e.name + ", " + e.message);
                    }
                }
            }
            // resume game
            tgfFunctions.resumeAll();
            _this._DOMUpdating = false;
        }, 100);
    };
    // Updates the style element of the canvas with the given depth index
    TGFGraphics.prototype._updateCanvasCSS = function (canvas, depthIndex) {
        var left, top;
        if (this.scaleFactor === undefined) {
            return;
        }
        left = this.cssLeftOffset + canvas.canvasLeft;
        top = this.cssTopOffset + canvas.canvasTop;
        if (this.centerSquare) {
            var ratio = this.windowWidth / this.windowHeight;
            var squared_left = 0;
            var squared_top = 0;
            if (ratio <= 1) {
                squared_left = -Math.round(((this.scaleFactor * canvas.canvasWidth) - this.windowWidth) / 2);
                squared_top = 0;
            }
            else {
                ratio = 1;
                squared_left = 0;
                squared_top = 0;
            }
            tgfGraphics.squaredLeft = squared_left * this.scaleFactor;
            tgfGraphics.squaredTop = squared_top; // * this.scaleFactor;
            tgfGraphics.ratio = ((ratio < this.minRatio) ? this.minRatio : ratio);
            // transform instead of left/top
            canvas.setAttribute("style", "-webkit-transform: translate(" + squared_left + "px, " + squared_top + "px); transform: translate(" + squared_left + "px, " + squared_top + "px); position:absolute; left:" + 0 + "px; top:" + 0 + "px; width:"
                + Math.round(this.scaleFactor * canvas.canvasWidth) + "px; height:" + Math.round(this.scaleFactor * canvas.canvasHeight) + "px; z-index: " + (depthIndex + 1).toString());
            // canvas.setAttribute("style", "position:absolute; transform: translate(0px, 0px); left:" + squared_left + "px; top:" + squared_top + "px; width:" 
            // + Math.round(this.scaleFactor * canvas.canvasWidth) + "px; height:" + Math.round(this.scaleFactor * canvas.canvasHeight) + "px; z-index: " + (depthIndex+1).toString()); 
        }
        else {
            canvas.setAttribute("style", "position:absolute; left:" + Math.round(this.scaleFactor * left) + "px; width:"
                + Math.round(this.scaleFactor * canvas.canvasWidth) + "px; height:" + Math.round(this.scaleFactor * canvas.canvasHeight) + "px; z-index: " + (depthIndex + 1).toString());
        }
    };
    // Updates the style element of all the canvasses
    TGFGraphics.prototype._updateCanvassesCSS = function () {
        var i;
        for (i = 0; i < this.canvasses.length; i += 1) {
            this._updateCanvasCSS(this.canvasses[i], i);
        }
    };
    // Applies the properties to the specified canvas
    TGFGraphics.prototype._initializeCanvas = function (canvas, properties) {
        var top, left, width, height, canvasStatic;
        // check properties
        if (!properties) {
            properties = {};
        }
        top = (properties.canvasTop !== undefined) ? properties.canvasTop : 0;
        left = (properties.canvasLeft !== undefined) ? properties.canvasLeft : 0;
        width = (properties.canvasWidth !== undefined) ? properties.canvasWidth : this.areaWidth;
        height = (properties.canvasHeight !== undefined) ? properties.canvasHeight : this.areaHeight;
        canvasStatic = (properties.canvasStatic !== undefined) ? properties.canvasStatic : false;
        // set standard canvas properties	
        canvas.style.top = top + "px";
        canvas.style.left = left + "px";
        canvas.style.position = "absolute";
        canvas.width = width;
        canvas.height = height;
        // set own canvas properties
        canvas.canvasTop = top;
        canvas.canvasLeft = left;
        canvas.canvasWidth = width;
        canvas.canvasHeight = height;
        canvas.canvasStatic = canvasStatic;
        if (canvasStatic) {
            canvas.drawTokens = false;
        }
    };
    // Creates a canvas html element and adds it to page; returns the canvas
    TGFGraphics.prototype._createCanvas = function (properties) {
        // create canvas
        var canvas = document.createElement("canvas");
        this._initializeCanvas(canvas, properties);
        // add canvas to DOM
        if (this.container) {
            this.container.appendChild(canvas);
        }
        else {
            document.body.appendChild(canvas);
        }
        return canvas;
    };
    TGFGraphics.prototype.addCustomCanvas = function (canvas, properties) {
        this._initializeCanvas(canvas, properties);
        if (this.container) {
            this.container.appendChild(canvas);
        }
        else {
            document.body.appendChild(canvas);
        }
        this.canvasses.push(canvas);
        this._updateCanvasCSS(canvas, this.canvasses.length - 1);
    };
    // Destroy the canvas
    TGFGraphics.prototype._destroyCanvas = function (canvas) {
        // remove element from DOM
        if (this.container) {
            this.container.removeChild(canvas);
        }
        else {
            document.body.removeChild(canvas);
        }
    };
    // Find the index of a canvas in the array. -1 if it does not exist.
    TGFGraphics.prototype._findIndex = function (canvas) {
        var i;
        if (!canvas) {
            return -1;
        }
        for (i = 0; i < this.canvasses.length; i += 1) {
            if (this.canvasses[i] === canvas) {
                return i;
            }
        }
        return -1;
    };
    // ***************************************
    // Adding and removing canvasses
    // ***************************************
    // Adds a canvas on top of the existing ones; returns the canvas
    TGFGraphics.prototype.addCanvas = function (properties) {
        var newCanvas;
        newCanvas = this._createCanvas(properties);
        this.canvasses.push(newCanvas);
        this._updateCanvasCSS(newCanvas, this.canvasses.length - 1);
        return newCanvas;
    };
    // Inserts a canvas below the given one; returns the canvas
    TGFGraphics.prototype.insertCanvas = function (targetCanvas, properties) {
        var index, newCanvas;
        index = this._findIndex(targetCanvas);
        if (index < 0) {
            index = 0;
        }
        newCanvas = this._createCanvas(properties);
        this.canvasses.splice(index, 0, newCanvas);
        this._updateCanvassesCSS();
        return newCanvas;
    };
    // Removes the given canvas; returns whether found and removed
    TGFGraphics.prototype.removeCanvas = function (canvas) {
        var index;
        // Cannot remove the main canvas
        if (canvas === this.mainCanvas) {
            return false;
        }
        index = this._findIndex(canvas);
        if (index < 0) {
            return false;
        }
        this.canvasses.splice(index, 1);
        // if removing backgroundCanvas update pointer
        if (canvas === this.backgroundCanvas) {
            this.backgroundCanvas = undefined;
        }
        this._destroyCanvas(canvas);
        // update css settings for the remaining canvasses
        this._updateCanvassesCSS();
        return true;
    };
    // Updates the position of the canvas relative to the main canvas
    TGFGraphics.prototype.moveCanvas = function (canvas, top, left) {
        if (!canvas) {
            return;
        }
        canvas.canvasTop = (top !== undefined && top !== null) ? top : canvas.canvasTop;
        canvas.canvasLeft = (left !== undefined && left !== null) ? left : canvas.canvasLeft;
        this._updateCanvasCSS(canvas, this._findIndex(canvas));
    };
    // Updates the width and height of the specified canvas, note that this might clear the canvas
    TGFGraphics.prototype.resizeCanvas = function (canvas, width, height) {
        if (!canvas) {
            return;
        }
        canvas.canvasWidth = (width !== undefined && width !== null) ? width : canvas.canvasWidth;
        canvas.canvasHeight = (height !== undefined && height !== null) ? height : canvas.canvasHeight;
        canvas.width = canvas.canvasWidth;
        canvas.height = canvas.canvasHeight;
        this._updateCanvasCSS(canvas, this._findIndex(canvas));
        // Make sure tbGraphics knows the correct size
        if (tbGraphics.canvas === canvas) {
            tbGraphics.setCanvas(canvas);
        }
    };
    // ***************************************
    // Retrieving and clearing canvasses
    // ***************************************
    // Returns an array containing all canvasses ordered from lowest to highest
    TGFGraphics.prototype.getAllCanvasses = function () {
        var i, res = [];
        for (i = 0; i < this.canvasses.length; i += 1) {
            res.push(this.canvasses[i]);
        }
        return res;
    };
    // Returns an array containing all static canvasses ordered from lowest to highest
    TGFGraphics.prototype.getStaticCanvasses = function () {
        var i, res = [];
        for (i = 0; i < this.canvasses.length; i += 1) {
            if (this.canvasses[i].canvasStatic) {
                res.push(this.canvasses[i]);
            }
        }
        return res;
    };
    // Returns an array containing all dynamic canvasses ordered from lowest to highest
    TGFGraphics.prototype.getDynamicCanvasses = function () {
        var i, res = [];
        for (i = 0; i < this.canvasses.length; i += 1) {
            if (!this.canvasses[i].canvasStatic) {
                res.push(this.canvasses[i]);
            }
        }
        return res;
    };
    // Sets a canvas to be static
    TGFGraphics.prototype.setCanvasStatic = function (canvas) {
        canvas.canvasStatic = true;
        canvas.drawTokens = false;
    };
    // Sets a canvas to be dynamic
    TGFGraphics.prototype.setCanvasDynamic = function (canvas) {
        canvas.canvasStatic = false;
        canvas.drawTokens = true;
    };
    // Clear a given canvas. If color is defined, that color is used for clearing
    TGFGraphics.prototype.clearCanvas = function (canvas, color) {
        if (tbGraphics.webGLAvailable) {
            return;
        }
    };
    TGFGraphics.prototype.render = function () {
        this.renderer["render"](this.pixiContainer);
    };
    TGFGraphics.prototype.addChild = function (obj) {
        this.pixiContainer["addChild"](obj);
    };
    TGFGraphics.prototype.addChildAt = function (obj, index) {
        this.pixiContainer["addChildAt"](obj, index);
    };
    TGFGraphics.prototype.removeChild = function (obj) {
        this.pixiContainer["removeChild"](obj);
    };
    // Clear all canvasses. If color is defined, that color is used for clearing
    TGFGraphics.prototype.clearAllCanvasses = function (color) {
        var i;
        for (i = 0; i < this.canvasses.length; i += 1) {
            this.clearCanvas(this.canvasses[i], color);
        }
    };
    // Clear all static canvasses. If color is defined, that color is used for clearing
    TGFGraphics.prototype.clearStaticCanvasses = function (color) {
        var i;
        for (i = 0; i < this.canvasses.length; i += 1) {
            if (this.canvasses[i].canvasStatic) {
                this.clearCanvas(this.canvasses[i], color);
            }
        }
    };
    // Clear all dynamic canvasses. If color is defined, that color is used for clearing
    TGFGraphics.prototype.clearDynamicCanvasses = function (color) {
        var i;
        for (i = 0; i < this.canvasses.length; i += 1) {
            if (!this.canvasses[i].canvasStatic) {
                this.clearCanvas(this.canvasses[i], color);
            }
        }
    };
    // ***************************************
    // Changing the depth order of the canvasses
    // ***************************************
    // Move the given canvas before the specified target canvas
    TGFGraphics.prototype.moveBefore = function (canvas, targetCanvas) {
        var targetIndex, canvasIndex;
        if (this.canvasses.length <= 1 || !canvas || canvas === targetCanvas) {
            return;
        }
        // find canvas and remove it from array array
        canvasIndex = this._findIndex(canvas);
        if (canvasIndex < 0) {
            return;
        }
        this.canvasses.splice(canvasIndex, 1);
        // find target canvas position in array
        targetIndex = this._findIndex(targetCanvas);
        if (targetIndex < 0) {
            targetIndex = this.canvasses.length - 1;
        }
        // add canvas in front of target canvas (so behind it in the array)
        this.canvasses.splice(targetIndex + 1, 0, canvas);
        this._updateCanvassesCSS();
    };
    // Move the given canvas in front of all other canvasses
    TGFGraphics.prototype.moveToFront = function (canvas) {
        this.moveBefore(canvas);
    };
    // Move the given canvas right behind the specified canvas
    TGFGraphics.prototype.moveBehind = function (canvas, targetCanvas) {
        var targetIndex, canvasIndex;
        if (this.canvasses.length <= 1 || !canvas || canvas === targetCanvas) {
            return;
        }
        // find canvas and remove it from array array
        canvasIndex = this._findIndex(canvas);
        if (canvasIndex < 0) {
            return;
        }
        this.canvasses.splice(canvasIndex, 1);
        // find target canvas position in array
        targetIndex = this._findIndex(targetCanvas);
        if (targetIndex < 0) {
            targetIndex = 0;
        }
        // add canvas behind target canvas (so before the target canvas in the array)
        this.canvasses.splice(targetIndex, 0, canvas);
        this._updateCanvassesCSS();
    };
    // Move the given canvas behind all other canvasses
    TGFGraphics.prototype.moveToBack = function (canvas) {
        this.moveBehind(canvas);
    };
    // ***************************************
    // Useful other methods
    // ***************************************
    // Translates the specified x coordinate based on the sourceCanvas to the x coordinate of
    // the same location on the targetCanvas
    TGFGraphics.prototype.translateCoordinateX = function (sourceCanvas, targetCanvas, x) {
        if (!sourceCanvas || !targetCanvas) {
            return x;
        }
        return sourceCanvas.canvasLeft - targetCanvas.canvasLeft + x;
    };
    // Translates the specified y coordinate based on the sourceCanvas to the y coordinate of
    // the same location on the targetCanvas
    TGFGraphics.prototype.translateCoordinateY = function (sourceCanvas, targetCanvas, y) {
        if (!sourceCanvas || !targetCanvas) {
            return y;
        }
        return sourceCanvas.canvasTop - targetCanvas.canvasTop + y;
    };
    // Set the url of the background image of the game page
    TGFGraphics.prototype.setBackgroundImage = function (url) {
        document.body["style"]["backgroundImage"] = "url(" + url + ")";
    };
    // Set the background color of the game page
    TGFGraphics.prototype.setBackgroundColor = function (color) {
        document.body["style"]["backgroundColor"] = color;
    };
    // Set the safe zone
    TGFGraphics.prototype.setSafeZone = function (left, top, width, height) {
        this.safeZoneLeft = (left === undefined) ? this.safeZoneLeft : left;
        this.safeZoneTop = (top === undefined) ? this.safeZoneTop : top;
        this.safeZoneWidth = (width === undefined) ? this.safeZoneWidth : width;
        this.safeZoneHeight = (height === undefined) ? this.safeZoneHeight : height;
        this._updateViewport(true);
    };
    // Set the safe zone anchors
    TGFGraphics.prototype.setSafeZoneAnchors = function (horizontal, vertical) {
        this.safeZoneHorizontalAnchor = (horizontal === undefined) ? this.safeZoneHorizontalAnchor : horizontal;
        this.safeZoneVerticalAnchor = (vertical === undefined) ? this.safeZoneVerticalAnchor : vertical;
        this._updateViewport(true);
    };
    // Set whether the canvasses must be scaled
    TGFGraphics.prototype.setScaling = function (scaleUp, scaleDown) {
        this.allowScalingUp = (scaleUp === undefined) ? this.allowScalingUp : scaleUp;
        this.allowScalingDown = (scaleDown === undefined) ? this.allowScalingDown : scaleDown;
        this._updateViewport(true);
    };
    // Set the area that the canvasses can cover
    TGFGraphics.prototype.setArea = function (width, height) {
        this.areaWidth = (width === undefined) ? this.areaWidth : width;
        this.areaHeight = (height === undefined) ? this.areaHeight : height;
        this._updateViewport(true);
    };
    TGFGraphics.prototype.getScreenLeft = function () {
        return tgfGraphics.areaWidth / 2 - tgfGraphics.visibleWidth / 2;
    };
    TGFGraphics.prototype.getScreenRight = function () {
        return tgfGraphics.areaWidth / 2 + tgfGraphics.visibleWidth / 2;
    };
    TGFGraphics.prototype.getHalfScreenWidth = function () {
        return tgfGraphics.visibleWidth / 2;
    };
    TGFGraphics.prototype.getHalfScreenHeight = function () {
        return tgfGraphics.visibleHeight / 2;
    };
    return TGFGraphics;
}());
export { TGFGraphics };
// ***************************************
// Create the instance
// ***************************************
export var tgfGraphics = new TGFGraphics();
